import { Component, ComponentRef, Injectable } from '@angular/core';

import { DetachedData } from '@app/lm-route-resuse-strategy';
import { ActivatedRouteSnapshot, DetachedRouteHandle, Router, UrlSegment, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { LMRouteComponentService } from '@services/lm-route-component.service';


interface ExtendedDetachedRouteHandle extends DetachedRouteHandle {
  componentRef?: ComponentRef<any>;
}

@Injectable()
export class LMRouteReuseCacheService {

  private routeCache = new Map<string, DetachedData>();
  private max = 20;
  public static DELIMITER: string = '-';
  public static supportedComponents = ['form', 'facade', 'list'];
  private enteredIndex = 0;

  constructor(
    private currentComponent: LMRouteComponentService,
  ) {}

  public getCompFromRoute(route: ActivatedRouteSnapshot): Component {
    let retComp : Component;
    const key: string = this.getKeyFromRoute(route);
    const routeCacheData: DetachedData | undefined = this.routeCache.get(key);
    try {
      retComp =  routeCacheData!.component;
    } catch (e) {
  //    let test = this.currentComponent.getRouteComponent() instanceof LMFormComponent
      retComp = this.currentComponent.getRouteComponent() as Component;
    }
    return retComp;
  }

  public getKeyFromRoute(route: ActivatedRouteSnapshot, delimiter?: string): string {
    // this need to be rewritten. done for a quick test.
    let key = '';
    if (route.url?.length > 0) {
      const segments: UrlSegment[] = route.url;
      key = this.getKeyFromUrlSegments(segments, delimiter);
    }
    const action: string = route.queryParams.action;
    const userFolder: string = route.queryParams.userfolder;

    const delimiterToUse = delimiter ? delimiter :  LMRouteReuseCacheService.DELIMITER;
    key = key + (action && action.length > 0 ? delimiterToUse + action : '');
    return key + (userFolder && userFolder.length > 0 ? delimiterToUse + userFolder : '');
  }

  private getKeyFromUrlSegments(segments: UrlSegment[], delimiter?: string): string {
    let routeString = '';
    const delimiterToUse = delimiter ? delimiter :  LMRouteReuseCacheService.DELIMITER;
    segments.forEach((segment, index) => {
      if (segment?.path.indexOf(":") >= 0) {
        return routeString
      }
      if(index ===3 && segment?.path?.indexOf("(") >= 0) {
        routeString += segment.path.replace(/\([^)]*\)/g, '()') + delimiterToUse;
      }
      else {
        routeString += segment.path + delimiterToUse;
      }
    });
    // Remove the trailing slash
    routeString = routeString.slice(0, -1);
    return routeString;
  }

  public getRecordKeysFromSegment(inputString: string): string {
    const matches = inputString.match(/\((.*?)\)/g); // Use non-greedy match to capture content between parentheses
    if (matches) {
      return matches.map(match => match.slice(1, -1))[0]; // Remove parentheses from matches
    }
    return '';
  }

   public doesRouteHandleKeyMatchRouter (route: ActivatedRouteSnapshot, router: Router, routeHandleKey: string, delimiter?: string): boolean {
     let routeKey = this.getKeyFromUrlSegments(this.parseUrlToSegments(router.url, router), delimiter);
     const action: string = route.queryParams.action;
     const delimiterToUse = delimiter ? delimiter :  LMRouteReuseCacheService.DELIMITER;
     routeKey = routeKey + (action && action.length > 0 ? delimiterToUse + action : '');
     return routeKey === routeHandleKey;
 }

  public parseUrlToSegments(urlString: string, router: Router): UrlSegment[] {
    const urlTree: UrlTree = router.parseUrl(urlString);
    return urlTree.root.children['primary']?.segments || [];
  }

  public getRecordResource(route: ActivatedRouteSnapshot): string {
    const segments: UrlSegment[] = route.url;
    return segments[3].path;
  }

  public hasHandle(route: ActivatedRouteSnapshot): boolean {
    let handle: DetachedRouteHandle | undefined = this.getHandle(route);
    return handle !== undefined;
  }

  public getHandle(route: ActivatedRouteSnapshot): DetachedRouteHandle | undefined {
    const key: string = this.getKeyFromRoute(route);
    const handle: DetachedRouteHandle | undefined = this.routeCache.get(key)?.handle;
    return handle;
  }

  private getParams(route: ActivatedRouteSnapshot): DetachedRouteHandle | undefined {
    const key: string = this.getKeyFromRoute(route);
    const params: DetachedRouteHandle | undefined = this.routeCache.get(key)?.handle;
    return params;
  }

  private isRouteForm(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(LMRouteReuseCacheService.DELIMITER + 'form' + LMRouteReuseCacheService.DELIMITER) > 0;
  }

  private isRouteList(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(LMRouteReuseCacheService.DELIMITER + 'list' + LMRouteReuseCacheService.DELIMITER) > 0;
  }

  private isRoutePage(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(LMRouteReuseCacheService.DELIMITER + 'page' + LMRouteReuseCacheService.DELIMITER) > 0;
  }

  private isRouteReport(route: ActivatedRouteSnapshot): boolean {
    const key: string = this.getKeyFromRoute(route);
    return key.indexOf(LMRouteReuseCacheService.DELIMITER + 'report' + LMRouteReuseCacheService.DELIMITER) > 0;
  }

  public addRouteItem(key: string, handle: DetachedRouteHandle, route: ActivatedRouteSnapshot) {
    const verifyCount: DetachedData | undefined = this.routeCache.get(key);
    const count = verifyCount ? verifyCount.count+1 : 1;

    if (this.routeCache.size >= this.max) {
      if (!environment.production) {
        console.log('**ROUTEREUSE** Adding route eceeds MAX and 1 currntly cached will be dropped');
      }
      const removeList = Array.from(this.routeCache);
      removeList.sort((a, b) => ((b[1].timestamp as number) - (a[1].timestamp as number)));
      //removeList.sort((a, b) => 4);
      for (let x = 0; x < removeList.length; x++) {
        removeList[ x ][ 1 ].points = (x + 1) + (removeList[ x ][ 1 ].count * 2);
      }
      removeList.sort((a, b) => ((a[ 1 ].points as number) - (b[ 1 ].points as number)));

      this.routeCache = this.arrayToMap(removeList.slice(1));
    }
    const d = new Date();
    let currentComponent: Component;

    try {
      currentComponent = this.getCompFromRoute(route);
    } catch (e) {
      currentComponent = this.currentComponent.getRouteComponent();
    }

    this.routeCache.set(key, { timestamp: d.getTime(), handle: handle, component: currentComponent, params: route.params, count: count, enteredIndex: this.enteredIndex++, points: 0 });
  }

  private  arrayToMap(array: [ key: string, detachedData: DetachedData ][]): Map<string, DetachedData> {
    const map = new Map<string, DetachedData>();

    // Loop through the array and add key-value pairs to the map
    array.forEach(item => {
      map.set(item[0], item[1]);
    });

    return map;
  }
  public isReusableContent(route: ActivatedRouteSnapshot): boolean {
    const key = this.getKeyFromRoute(route);
    const DELIMITER = LMRouteReuseCacheService.DELIMITER;
    return LMRouteReuseCacheService.supportedComponents.some(
      type => key.includes(DELIMITER + type + DELIMITER)
    );
  }

// Method to remove a member by string key
  public removeRouteHandleFromCache(route: ActivatedRouteSnapshot): boolean  {
    const key: string = this.getKeyFromRoute(route);
    if (this.routeCache.has(key)) {
      this.routeCache.delete(key);
      // when we remove the route from the cache we need to destroy the component that was stored and allow it to clean up and not leave any loose ends.
      this.removeStoredRoute(route);
      return true;
    } else {
      return false;
    }
  }

  // Method to clean up components that are stored and have been removed from our cache to clean up any listeners and memory. This will run the NgDestroy on the component
  private removeStoredRoute(route: ActivatedRouteSnapshot): void {
    const handle = this.getHandle(route);
    if (handle) {
      // Cast to access componentRef and destroy
      const extendedHandle = handle as ExtendedDetachedRouteHandle;
      if (extendedHandle.componentRef) {
        extendedHandle.componentRef.destroy();
      }
    }
  }
}
